/**
 * SurveyQuestions
 *
 * Editor for up to three survey questions with add/remove/edit and AI labels.
 */
import React from 'react';
import { Plus, X, Sparkles } from 'lucide-react';
import { Label } from '../../ui/inputs/label';
import { Input } from '../../ui/inputs/input';

export function SurveyQuestions({
  surveyQuestions,
  handleAddQuestion,
  handleRemoveQuestion,
  handleQuestionChange,
  setIsHelpOpen,
  aiGeneratedFields,
  className = '',
  readOnly = false
}) {
  // Maximum number of questions allowed
  const MAX_QUESTIONS = 3;
  
  // Debug logging for surveyQuestions
  React.useEffect(() => {
    console.log('SurveyQuestions rendering with:', {
      questionCount: surveyQuestions?.length || 0,
      questions: surveyQuestions?.map(q => ({
        id: q.id,
        hasContent: Boolean(q.question && q.question.trim()),
        contentPreview: q.question ? 
          (q.question.length > 20 ? q.question.substring(0, 20) + '...' : q.question) 
          : 'empty'
      })) || [],
      aiGeneratedFields
    });
  }, [surveyQuestions, aiGeneratedFields]);
  
  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-y-3 sm:gap-y-0">
        <div>
          <Label className="flex items-center gap-1.5">
            Survey Questions
            <button
              type="button"
              onClick={() => setIsHelpOpen(true)}
              className="inline-flex items-center justify-center text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12" y2="17" />
              </svg>
            </button>
          </Label>
          <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mt-1 flex items-center">
            {readOnly ? 'View-only mode' : (
              <>
                <span className="text-red-500 mr-1">*</span>
                <span>At least one question required</span>
                <span className="text-gray-500 dark:text-gray-400 font-normal ml-1">(maximum {MAX_QUESTIONS})</span>
              </>
            )}
          </div>
        </div>
        {!readOnly && surveyQuestions.length < MAX_QUESTIONS && (
          <button
            type="button"
            onClick={handleAddQuestion}
            className="sm:ml-auto w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            Add Question
          </button>
        )}
        {!readOnly && surveyQuestions.length >= MAX_QUESTIONS && (
          <div className="sm:ml-auto text-xs text-gray-500 dark:text-gray-400 italic">
            Maximum {MAX_QUESTIONS} questions allowed
          </div>
        )}
      </div>

      <div className="space-y-5 sm:space-y-4 mt-4 sm:mt-0">
        {surveyQuestions.map((q, index) => {
          const isAiGenerated = aiGeneratedFields?.[`question_${index}`];
          return (
            <div
              key={q.id}
              className="relative flex flex-col space-y-3 sm:space-y-2 rounded-lg border border-gray-200 bg-white p-5 sm:p-4 dark:border-gray-800 dark:bg-gray-900"
            >
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-0">
                <Label
                  htmlFor={`question-${q.id}`}
                  className="mb-1 sm:mb-2 flex items-center text-gray-700 dark:text-gray-200"
                >
                  Question {index + 1}
                  {isAiGenerated && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
                      <Sparkles className="mr-1 h-3 w-3" />
                      AI Generated
                    </span>
                  )}
                </Label>
                
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => handleRemoveQuestion(q.id)}
                    className="self-end sm:self-auto sm:ml-auto rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-400"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              <Input
                id={`question-${q.id}`}
                value={q.question}
                onChange={(e) => handleQuestionChange(q.id, e.target.value)}
                placeholder="Enter your survey question"
                className="w-full"
                disabled={readOnly}
              />
            </div>
          );
        })}

        {surveyQuestions.length === 0 && (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center dark:border-gray-700 dark:bg-gray-900">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {readOnly 
                ? 'No survey questions have been added.' 
                : 'No survey questions yet. Click the "Add Question" button to get started.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}